import { useEffect, useState } from "react";

import { api, ApiError } from "../lib/api";
import { useAuth } from "../context/auth";
import { useLive } from "../lib/use-live";
import { relative } from "../lib/format";
import type { RulesGuide, RulesGuideDocument, Settings } from "../types/api";
import { useT } from "../context/locale";
import { useToast } from "../context/toast";
import { Badge, Button, ErrorNote, Panel } from "../components/ui";
import {
  SegmentedControl,
  TextAreaField,
  TextField,
} from "../components/ui/form";

const KEYS = {
  noticeEn: "registration.review_notice_en",
  noticeZh: "registration.review_notice_zh",
  reviewUrl: "registration.review_contact_url",
  reviewNum: "registration.review_contact_num",
  supportEn: "support.message_en",
  supportZh: "support.message_zh",
  supportUrl: "support.contact_url",
  supportNum: "support.contact_num",
} as const;

type Field = keyof typeof KEYS;
type Draft = Record<Field, string>;

const EMPTY: Draft = {
  noticeEn: "",
  noticeZh: "",
  reviewUrl: "",
  reviewNum: "",
  supportEn: "",
  supportZh: "",
  supportUrl: "",
  supportNum: "",
};

type GuideLocale = "en" | "zh";

const EMPTY_GUIDE: RulesGuideDocument = { title: "", intro: "", sections: [] };
const EMPTY_GUIDE_DRAFT: Record<GuideLocale, RulesGuideDocument> = {
  en: EMPTY_GUIDE,
  zh: EMPTY_GUIDE,
};

/**
 * Notifications — the copy players are shown.
 *
 * Editable here by design: PM spec §02 requires the review notice to be
 * changeable without a deployment, because it is the only thing a player sees
 * between submitting a registration and being approved. The support message and
 * the two links are the same idea applied to the funding route — with no payment
 * gateway, that copy *is* how a player tops up or withdraws. The rules booklet
 * at the bottom is the same idea again, applied to the popup that explains the
 * game: a section an operator cannot add without a release is a section that
 * never gets added.
 *
 * **Read-only until you press Edit.** Every panel in this console is a live
 * subscription, which is right for a table and wrong for a form: the server
 * re-answers on its own schedule, and a screen that reseeded its inputs from
 * each answer wiped whatever the operator was halfway through typing. Reading
 * and writing are now distinct modes, so an incoming change can never land in
 * the middle of an edit — and the moment you press Save it goes back to
 * following the server again.
 *
 * One Edit and one Save cover both halves of the screen, because they are two
 * halves of one job: the API keeps them apart, as two routes with different
 * validation, but an operator sitting down to fix the player-facing copy should
 * not have to notice where that boundary falls.
 */
export function Notifications() {
  const t = useT();
  const toast = useToast();
  const { allowed } = useAuth();
  const settings = useLive<Settings>("settings", {});
  const guide = useLive<RulesGuide>("rules-guide", {});

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [guideDraft, setGuideDraft] =
    useState<Record<GuideLocale, RulesGuideDocument>>(EMPTY_GUIDE_DRAFT);
  const [guideLocale, setGuideLocale] = useState<GuideLocale>("en");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Whether this operator may change any of it.
   *
   * The API refuses a write from anyone without `settings:update` either way —
   * this is so the console does not offer a button whose only outcome is a
   * refusal. The grid is watched, not read once at sign-in, so a permission
   * revoked mid-session takes the Edit button away rather than leaving it there
   * to be discovered by pressing it.
   */
  const canEdit = allowed("settings", "update");

  /**
   * Follow the server, except while an edit is open.
   *
   * The guard is the whole point: without it a live push or the ten-second
   * heartbeat would overwrite the draft mid-sentence, and pressing Save would
   * then store whatever the server already had — which reads as the console
   * ignoring you and the API serving hardcoded copy.
   */
  useEffect(() => {
    if (editing || !settings.data) return;
    const stored = settings.data;
    setDraft(
      Object.fromEntries(
        Object.entries(KEYS).map(([field, key]) => [field, String(stored[key] ?? "")]),
      ) as Draft,
    );
  }, [settings.data, editing]);

  useEffect(() => {
    if (editing || !guide.data) return;
    setGuideDraft({
      en: clone(guide.data.documents.en),
      zh: clone(guide.data.documents.zh),
    });
  }, [guide.data, editing]);

  const set = (field: Field) => (value: string) =>
    setDraft((current) => ({ ...current, [field]: value }));

  /** Edits one locale's booklet, leaving the other language untouched. */
  const setGuide = (change: (document: RulesGuideDocument) => RulesGuideDocument) =>
    setGuideDraft((current) => ({ ...current, [guideLocale]: change(current[guideLocale]) }));

  function startEditing() {
    setError(null);
    setEditing(true);
  }

  /** Throws the draft away and picks up whatever the server holds now. */
  function cancel() {
    setError(null);
    setEditing(false);
    settings.reload();
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.updateSettings(
        Object.fromEntries(
          Object.entries(KEYS).map(([field, key]) => [key, draft[field as Field]]),
        ),
      );
      /*
       * Two calls, because the API deliberately keeps them apart: the notices
       * are a flat key/value record and the booklet is a validated document.
       * Sequential rather than concurrent so a rejected booklet cannot leave
       * the operator unsure which half of their edit landed — the notices are
       * saved by the time the second call can fail, and the error names the
       * booklet.
       */
      const published = await api.updateRulesGuide({
        en: guideDraft.en,
        zh: guideDraft.zh,
      });

      toast.success(
        t("toast.notificationsSaved"),
        t("toast.rulesPublished", { version: published.version }),
      );
      // Back to following the server, which now agrees with what was just sent.
      setEditing(false);
      settings.reload();
      guide.reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("error.save"));
    } finally {
      setBusy(false);
    }
  }

  const document = guideDraft[guideLocale];

  return (
    <div className="max-w-3xl space-y-5">
      {settings.error ? (
        <ErrorNote message={settings.error} onRetry={settings.reload} />
      ) : null}
      {guide.error ? <ErrorNote message={guide.error} onRetry={guide.reload} /> : null}

      <div className="flex items-center justify-between gap-3">
        <p className="text-muted text-[13px]">
          {editing
            ? t("notifications.editingHint")
            : canEdit
              ? t("notifications.viewingHint")
              : t("notifications.readOnlyHint")}
        </p>
        <div className="flex items-center gap-2">
          {editing ? (
            <Badge tone="gold">{t("notifications.editing")}</Badge>
          ) : (
            <Badge tone="neutral">{t("notifications.live")}</Badge>
          )}
          {editing ? (
            <>
              <Button variant="ghost" onClick={cancel} disabled={busy}>
                {t("action.cancel")}
              </Button>
              <Button variant="gold" onClick={save} loading={busy}>
                {t("notifications.save")}
              </Button>
            </>
          ) : canEdit ? (
            <Button onClick={startEditing}>{t("action.edit")}</Button>
          ) : null}
        </div>
      </div>

      <Panel eyebrow={t("nav.players")} title={t("notifications.title")}>
        <div className="space-y-4">
          <p className="text-muted text-[13px]">{t("notifications.reviewExplain")}</p>
          <TextAreaField
            label={t("notifications.english")}
            value={draft.noticeEn}
            onChange={set("noticeEn")}
            disabled={!editing}
            required
          />
          <TextAreaField
            label={t("notifications.chinese")}
            value={draft.noticeZh}
            onChange={set("noticeZh")}
            disabled={!editing}
            required
          />
          <TextField
            label={t("notifications.reviewUrl")}
            value={draft.reviewUrl}
            onChange={set("reviewUrl")}
            hint={t("notifications.reviewUrlHint")}
            disabled={!editing}
          />
          <TextField
            label={t("notifications.reviewNum")}
            value={draft.reviewNum}
            onChange={set("reviewNum")}
            hint={t("notifications.reviewNumHint")}
            disabled={!editing}
          />
        </div>
      </Panel>

      <Panel eyebrow={t("nav.players")} title={t("notifications.supportLink")}>
        <div className="space-y-4">
          <p className="text-muted text-[13px]">{t("notifications.supportExplain")}</p>
          <TextAreaField
            label={t("notifications.english")}
            value={draft.supportEn}
            onChange={set("supportEn")}
            disabled={!editing}
            required
          />
          <TextAreaField
            label={t("notifications.chinese")}
            value={draft.supportZh}
            onChange={set("supportZh")}
            disabled={!editing}
            required
          />
          <TextField
            label={t("notifications.contactUrl")}
            value={draft.supportUrl}
            onChange={set("supportUrl")}
            hint={t("notifications.contactHint")}
            disabled={!editing}
          />
          <TextField
            label={t("notifications.contactNum")}
            value={draft.supportNum}
            onChange={set("supportNum")}
            hint={t("notifications.contactNumHint")}
            disabled={!editing}
          />
        </div>
      </Panel>

      {/* ── The rules-and-tutorial booklet ────────────────────────────────── */}
      <Panel
        eyebrow={t("nav.players")}
        title={t("rules.title")}
        actions={
          guide.data ? (
            <div className="flex items-center gap-2">
              <Badge tone="neutral">
                {t("rules.version", { version: guide.data.version })}
              </Badge>
              {guide.data.updatedAt ? (
                <span className="text-dim text-[11px]">
                  {relative(guide.data.updatedAt)}
                </span>
              ) : null}
            </div>
          ) : null
        }
      >
        <div className="space-y-4">
          <p className="text-muted text-[13px]">{t("rules.explain")}</p>
          {editing ? (
            <p
              className="rounded-lg px-3 py-2 text-[12px]"
              style={{ background: "var(--warning-bg)", color: "var(--warning)" }}
            >
              {t("rules.versionWarning")}
            </p>
          ) : null}

          {/*
            One language at a time, rather than every field twice.
            Writing a booklet is a long form, and interleaving the two would
            double its height while making neither readable end to end. The
            draft holds both, so switching never loses what is not on screen.
          */}
          <SegmentedControl<GuideLocale>
            value={guideLocale}
            onChange={setGuideLocale}
            ariaLabel={t("rules.languageTabs")}
            options={[
              { value: "en", label: t("notifications.english") },
              { value: "zh", label: t("notifications.chinese") },
            ]}
          />

          <TextField
            label={t("rules.docTitle")}
            value={document.title}
            onChange={(title) => setGuide((current) => ({ ...current, title }))}
            hint={t("rules.docTitleHint")}
            disabled={!editing}
          />
          <TextAreaField
            label={t("rules.intro")}
            value={document.intro}
            onChange={(intro) => setGuide((current) => ({ ...current, intro }))}
            hint={t("rules.introHint")}
            disabled={!editing}
            rows={3}
          />

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="eyebrow">
                {t("rules.sections", { count: document.sections.length })}
              </p>
              {editing ? (
                <Button
                  size="sm"
                  onClick={() =>
                    setGuide((current) => ({
                      ...current,
                      sections: [...current.sections, { heading: "", body: "" }],
                    }))
                  }
                >
                  {t("rules.addSection")}
                </Button>
              ) : null}
            </div>

            {document.sections.length === 0 ? (
              <p className="text-dim text-[13px]">{t("rules.noSections")}</p>
            ) : (
              document.sections.map((section, index) => (
                <SectionEditor
                  // Positional: a section has no id of its own, and the list is
                  // short enough that a reorder re-rendering both rows costs
                  // nothing. Keying on the heading would remount the field on
                  // every keystroke and lose the caret.
                  key={index}
                  index={index}
                  section={section}
                  total={document.sections.length}
                  editing={editing}
                  onChange={(next) =>
                    setGuide((current) => ({
                      ...current,
                      sections: current.sections.map((existing, at) =>
                        at === index ? next : existing,
                      ),
                    }))
                  }
                  onMove={(by) =>
                    setGuide((current) => ({
                      ...current,
                      sections: moved(current.sections, index, index + by),
                    }))
                  }
                  onRemove={() =>
                    setGuide((current) => ({
                      ...current,
                      sections: current.sections.filter((_, at) => at !== index),
                    }))
                  }
                />
              ))
            )}
          </div>
        </div>
      </Panel>

      {error ? <ErrorNote message={error} /> : null}
    </div>
  );
}

/** One section of the booklet: a heading, a body, and where it sits. */
function SectionEditor({
  index,
  section,
  total,
  editing,
  onChange,
  onMove,
  onRemove,
}: {
  index: number;
  section: { heading: string; body: string };
  total: number;
  editing: boolean;
  onChange: (section: { heading: string; body: string }) => void;
  onMove: (by: -1 | 1) => void;
  onRemove: () => void;
}) {
  const t = useT();

  return (
    <div
      className="space-y-3 rounded-lg border p-3"
      style={{ borderColor: "var(--border)" }}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="eyebrow">{t("rules.sectionNo", { number: index + 1 })}</span>
        {editing ? (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onMove(-1)}
              disabled={index === 0}
            >
              ↑
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onMove(1)}
              disabled={index === total - 1}
            >
              ↓
            </Button>
            <Button size="sm" variant="ghost" onClick={onRemove}>
              {t("action.delete")}
            </Button>
          </div>
        ) : null}
      </div>

      <TextField
        label={t("rules.heading")}
        value={section.heading}
        onChange={(heading) => onChange({ ...section, heading })}
        disabled={!editing}
      />
      <TextAreaField
        label={t("rules.body")}
        value={section.body}
        onChange={(body) => onChange({ ...section, body })}
        disabled={!editing}
        rows={4}
      />
    </div>
  );
}

/** A copy deep enough that editing the draft never touches the served payload. */
function clone(document: RulesGuideDocument): RulesGuideDocument {
  return {
    title: document.title,
    intro: document.intro,
    sections: document.sections.map((section) => ({ ...section })),
  };
}

/** `list` with the item at `from` moved to `to`. Out-of-range moves are no-ops. */
function moved<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}
